import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { parseISO } from 'date-fns';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import WDT_CHARACTERS_COMPLETED_AND_TIME_SPENT_QUERY from 'graph/reports/wdt-characters-completed-and-time-spent-report-query';
import Popover from 'components/@common/Popover';
import BarAndLineChartCard from '../../../charts/BarAndLineChartCard';
import useReportQueryParams from '../../../hooks/use-report-query-params';
import { getQueryVariables } from '../../../utils/report-param-utils';
import { ReportEntityType } from 'components/ReportsModule/types';
import {
  ReportIntervalUnit,
  WdtCharactersCompletedAndTimeSpentIntervalData
} from 'types/schema-generated-types';

const formatUTC = (isoString: string, formatOptions = {}): string => {
  const dateFormatter = new Intl.DateTimeFormat('en-us', { timeZone: 'UTC', ...formatOptions });
  return dateFormatter.format(parseISO(isoString));
};

const INTERVAL_CATEGORY_MAP = {
  [ReportIntervalUnit.DAY]: (
    intervals: WdtCharactersCompletedAndTimeSpentIntervalData[]
  ): string[] =>
    intervals.map(({ interval }: { interval: string }) =>
      formatUTC(interval, { day: '2-digit', month: '2-digit', year: 'numeric' })
    ),
  [ReportIntervalUnit.WEEK]: (
    intervals: WdtCharactersCompletedAndTimeSpentIntervalData[]
  ): string[] =>
    intervals.map(({ interval }: { interval: string }) =>
      formatUTC(interval, { day: '2-digit', month: '2-digit', year: 'numeric' })
    ),
  [ReportIntervalUnit.MONTH]: (
    intervals: WdtCharactersCompletedAndTimeSpentIntervalData[]
  ): string[] =>
    intervals.map(({ interval }: { interval: string }) =>
      formatUTC(interval, { month: '2-digit', year: 'numeric' })
    )
};

const getSeries = (intervals: WdtCharactersCompletedAndTimeSpentIntervalData[]): any => {
  const { charactersCompletedSeries, averageMinutesSpentSeries } = intervals.reduce(
    (acc, interval) => {
      acc.charactersCompletedSeries.push(interval.charactersCompletedCount);
      acc.averageMinutesSpentSeries.push(interval.averageMinutesSpent);
      return acc;
    },
    {
      charactersCompletedSeries: [] as number[],
      averageMinutesSpentSeries: [] as number[]
    }
  );

  return [
    {
      name: 'Characters',
      type: 'column',
      data: charactersCompletedSeries
    },
    {
      name: 'Time Spent',
      type: 'line',
      data: averageMinutesSpentSeries
    }
  ];
};

const CharactersAndTimeSpentChart: React.FC = (): React.ReactElement => {
  const [{ entityId, entityType, dateRange }] = useReportQueryParams();
  const { t } = useTranslation(['Reports', 'ReportsHelpCommon', 'ReportsHelpCharts']);
  const [fetchReport, { loading, data }] = useLazyQuery(
    WDT_CHARACTERS_COMPLETED_AND_TIME_SPENT_QUERY
  );

  const {
    intervalUnit = ReportIntervalUnit.DAY,
    intervals = [],
    totalCharactersCompletedCount,
    averageMinutesSpent
  } = data?.wdtCharactersCompletedAndTimeSpentReport || {};

  const series = getSeries(intervals);
  const categories = INTERVAL_CATEGORY_MAP[intervalUnit as ReportIntervalUnit](intervals);

  const texts: { [key in ReportEntityType]?: any } = {
    [ReportEntityType.STUDENT]: {
      title: t('Characters & Time Spent'),
      titleHelp: t('wdtCharactersCompletedAndTimeSpentHelp', { ns: 'ReportsHelpCharts' }),
      charactersCompletedHelp: t('wdtStudentCharactersCompletedHelp', { ns: 'ReportsHelpCharts' }),
      timeSpent: t('Time Spent'),
      timeSpentHelp: t('wdtTimeSpentHelp', { ns: 'ReportsHelpCharts' })
    },
    [ReportEntityType.CLASSROOM]: {
      title: t('Characters & Average Time Spent'),
      titleHelp: t('wdtCharactersCompletedAndAverageTimeSpentHelp', { ns: 'ReportsHelpCharts' }),
      charactersCompletedHelp: t('wdtClassroomCharactersCompletedHelp', {
        ns: 'ReportsHelpCharts'
      }),
      timeSpent: t('Average Time Spent'),
      timeSpentHelp: t('wdtAverageTimeSpentHelp', { ns: 'ReportsHelpCharts' })
    }
  };

  useEffect(() => {
    fetchReport({ variables: getQueryVariables({ entityId, entityType, dateRange }) });
  }, [fetchReport, entityId, entityType, dateRange]);

  return (
    <BarAndLineChartCard
      title={texts[entityType]?.title}
      helpText={texts[entityType]?.titleHelp}
      loading={loading}
      series={series}
      categories={categories}
      renderTextInformation={(): React.ReactElement => (
        <Grid spacing={4} container wrap="nowrap" direction="row">
          <Grid item>
            <Popover description={texts[entityType]?.charactersCompletedHelp}>
              <Typography component="h4" variant="body2">
                {t('Characters Completed')}
              </Typography>
            </Popover>
            <Typography variant="h1" component="span">
              {totalCharactersCompletedCount}
            </Typography>
          </Grid>
          <Grid item>
            <Popover description={texts[entityType]?.timeSpentHelp}>
              <Typography component="h4" variant="body2">
                {texts[entityType]?.timeSpent}
              </Typography>
            </Popover>
            <Typography variant="h1" component="span">
              {t(`{{averageMinutesSpent}} minutes`, {
                averageMinutesSpent: averageMinutesSpent
              })}
            </Typography>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default React.memo(CharactersAndTimeSpentChart);
